import React, {
    useEffect,
    useState,
    Fragment,
    useMemo,
    useCallback,
} from 'react';
import { observer } from 'mobx-react';
import { Route, Routes, useNavigate, Navigate } from 'react-router-dom';
import Layout from './layouts/layout';
import TokenSignIn from './containers/TokenSignIn';
import DirectSignIn from './containers/DirectSignIn';
import { EVENTS } from './utils/constants';
import { ThemeProvider } from 'styled-components';
import {
    ThemeProvider as MuiThemeProvider,
    StyledEngineProvider,
} from '@mui/material/styles';
import { I18nextProvider } from 'react-i18next';
import DirectionProvider, {
    DIRECTIONS,
} from 'react-with-direction/dist/DirectionProvider';
import * as Sentry from '@sentry/react';
import Infosec from './containers/infosec';
import CMI5 from './containers/cmi5/rxd';
import { useStores } from './hooks/use-stores';
import { useLocation, useMatch } from 'react-router-dom';
import AppRouting from './containers/AppRouting';
import { ConfirmationModal } from '@awarego-ui/components';
import Demo from './containers/demo';
import { Snackbar } from '@awarego-ui/components';
import AuthRoute from './hocs/AuthRoute';
import SignIn from './containers/SignIn';
import CheckEmail from './containers/CheckEmail';
import EmailSignInError from './containers/EmailSignInError';
import SSO from './containers/SSO';
import PrivateRoute from './hocs/PrivateRoute';
import AssessmentResults from './containers/Assessments/AssessmentResults';
import { publicHRASubstrings } from './utils/constants';

(function () {
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;

    window.history.pushState = function (...args) {
        console.trace('[pushState]', args);
        return origPush.apply(this, args);
    };

    window.history.replaceState = function (...args) {
        console.trace('[replaceState]', args);
        return origReplace.apply(this, args);
    };
})();




function App() {
    const navigate = useNavigate();

    const {
        store,
        companyStore,
        authStore,
        commonStore,
        brandingStore,
        speechStore,
    } = useStores();
    const { theme, muiTheme, brandingLoaded } = brandingStore;
    const {
        appLoading,
        languagesLoaded,
        i18n,
        messageShown,
        messageType,
        message,
    } = commonStore;
    const {
        isGuest,
        guestHomePath,
        company_id,
        subject_id,
        token,
        currentUser,
    } = authStore;
    const infosecMatch = useMatch('/mini-hra');
    const location = useLocation();

    const miniHRAtype = useMemo(() => {
        const type = publicHRASubstrings.find((x) =>
            location.pathname.startsWith(`/${x}`)
        );
        brandingStore.setDefaultMiniHraType(type);
        return type;
    }, [location.pathname]);

    const { currentCompany } = companyStore;
    const [companyId, setCompanyId] = useState();

    const {
        confirmationRequired,
        confirmationDescription,
        confirmationAction,
        confirmationTitle,
        confirmationShowCancel,
        confirmationCancelText,
        additionalCheckbox,
        isConfirmationDelete,
    } = commonStore;

    const changeRef = useCallback((element) => {
        speechStore.setElement(element);
    }, []);

    useEffect(() => {
        return () => {
            speechStore.setElement(null);
        };
    }, []);

    useEffect(() => {
        if (
            companyId &&
            currentCompany &&
            companyId !== currentCompany.company_id
        ) {
            navigate('/');
        }
        setCompanyId(currentCompany && currentCompany.company_id);
    }, [companyId, currentCompany, navigate]);

    const trainingMatch = useMatch(`/trainings/:trainingId`);

    useEffect(() => {
        if (currentCompany && currentUser) {
            //process utm when both company and user initialized
            const queryParams = new URLSearchParams(location.search);
            let utmSource, utmTerm;
            if (queryParams.has('utm_source')) {
                utmSource = queryParams.get('utm_source');
                queryParams.delete('utm_source');
            }
            if (queryParams.has('utm_term')) {
                utmTerm = queryParams.get('utm_term');
                queryParams.delete('utm_term');
            }
            if (utmSource || utmTerm) {
                const trainingId = trainingMatch
                    ? trainingMatch.params.trainingId
                    : queryParams.get('t');
                store.onLogEvent(trainingId, null, EVENTS.NOTIFICATION_LINK, {
                    utm_source: utmSource,
                    utm_term: utmTerm,
                });
                navigate(`?${queryParams.toString()}`, { replace: true });
            }
        }
    }, [currentCompany, currentUser, trainingMatch, navigate, store]);

    useEffect(() => {
        (async () => {
            if (token) {
                try {
                    await Promise.all([
                        authStore.pullUser(),
                        commonStore.loadLanguages(),
                    ]);
                } finally {
                    commonStore.setAppLoaded();
                }
            } else {
                await Promise.all([
                    brandingStore.loadBranding(),
                    commonStore.loadLanguages(),
                ]);
                await commonStore.setAppLoaded();
            }
        })();
    }, [authStore, brandingStore, commonStore, token]);

    const doConfirm = () => {
        commonStore.doConfirm();
    };

    const cancelConfirm = () => {
        commonStore.cancelConfirm();
    };

    const handleSignout = () => {
        authStore.logout();
    };
    const closeSnackbar = () => {
        commonStore.hideMessage();
    };

    const sendFeedback = async () => {
        const e = await Sentry.captureMessage('User Feedback Submitted');
        Sentry.withScope(function (scope) {
            scope.setTag('feedback', 'yes');
            // eslint-disable-next-line no-undef
            scope.setTag('modernizr.video', Modernizr.video);
            // eslint-disable-next-line no-undef
            scope.setTag('modernizr.video.h264', Modernizr.video.h264);
            // eslint-disable-next-line no-undef
            scope.setTag('modernizr.video.ogg', Modernizr.video.ogg);
            // eslint-disable-next-line no-undef
            scope.setTag('modernizr.video.vp9', Modernizr.video.vp9);
            // eslint-disable-next-line no-undef
            scope.setTag('modernizr.video.webm', Modernizr.video.webm);
            // eslint-disable-next-line no-undef
            scope.setTag('modernizr.video.hls', Modernizr.video.hls);
            scope.setLevel('warning');
            Sentry.showReportDialog({
                labelSubmit: 'Send Feedback',
                eventId: e,
            });
        });
    };

    const switchLanguage = async (language_code) => {
        if (authStore.isAuthenticated) {
            await store.setLanguage(language_code);
        }
    };

    const getDirection = () => {
        const languageObject = commonStore.languages.find((obj) => {
            return obj.code === store.language;
        });

        if (languageObject && languageObject.rtl) {
            return DIRECTIONS.RTL;
        }
        return DIRECTIONS.LTR;
    };

    return (
        <StyledEngineProvider injectFirst>
            <MuiThemeProvider theme={muiTheme}>
                <ThemeProvider theme={theme}>
                    {/*{!(languagesLoaded && brandingLoaded) && (*/}
                    {/*    <Waiting waitingActive={appLoading} from={'App'} />*/}
                    {/*)}*/}
                    {languagesLoaded && brandingLoaded && (
                        <I18nextProvider i18n={i18n}>
                            <DirectionProvider direction={getDirection()}>
                            <Fragment>
                                <audio ref={changeRef} />
                                <Routes>
                                    {/* Demo routes without Layout */}
                                    <Route
                                        path="/assessment/try-demo/:companyId/:reportTo"
                                        element={<Demo isAdmin isAssessment />}
                                    />
                                    <Route
                                        path="/assessment/demo/:companyId/:reportTo"
                                        element={<Demo isAssessment />}
                                    />
                                    <Route
                                        path="/training/try-demo/:companyId/:reportTo"
                                        element={<Demo isAdmin isTraining />}
                                    />
                                    <Route
                                        path="/training/demo/:companyId/:reportTo"
                                        element={<Demo isTraining />}
                                    />

                                    {/* All other routes wrapped in Layout */}
                                    <Route
                                        path="/*"
                                        element={
                                            <Layout
                                                sendFeedback={sendFeedback}
                                                handleSignout={handleSignout}
                                                switchLanguage={switchLanguage}
                                            />
                                        }
                                    >
                                        <Route
                                            path="infosec"
                                            element={<Navigate to="/mini-hra" />}
                                        />
                                        <Route
                                            path="human-risk-assessment"
                                            element={<Navigate to="/mini-hra" />}
                                        />
                                        <Route
                                            path="hra-demo"
                                            element={<Navigate to="/mini-hra" />}
                                        />
                                        <Route
                                            path="mini-hra/*"
                                            element={<Infosec />}
                                        />
                                        <Route
                                            path="h/:miniHRAKey/*"
                                            element={<Infosec />}
                                        />
                                        <Route
                                            path="assessment-results/:variant/:shareKey"
                                            element={<AssessmentResults />}
                                        />

                                        {publicHRASubstrings.map(
                                            (publicHRASubstring) => (
                                                <Route
                                                    key={publicHRASubstring}
                                                    path={publicHRASubstring}
                                                    element={
                                                        <Infosec
                                                            variant={brandingStore.publicHRAVariant(
                                                                publicHRASubstring
                                                            )}
                                                        />
                                                    }
                                                />
                                            )
                                        )}

                                        <Route
                                            path="auth/direct/signin/:token"
                                            element={<DirectSignIn />}
                                        />
                                        <Route
                                            path="auth/jwt/signin/:token"
                                            element={<TokenSignIn />}
                                        />
                                        <Route
                                            path="cmi5/:companyId/course/:uniqueId/:accessKey/au/:auId/*"
                                            element={<CMI5/>}
                                        />

                                        <Route
                                            path="auth/signin"
                                            element={
                                                <AuthRoute>
                                                    <SignIn
                                                        company_id={company_id}
                                                        subject_id={subject_id}
                                                    />
                                                </AuthRoute>
                                            }
                                        />
                                        <Route
                                            path="auth/check-email"
                                            element={
                                                <AuthRoute>
                                                    <CheckEmail />
                                                </AuthRoute>
                                            }
                                        />
                                        <Route
                                            path="auth/error/email"
                                            element={<EmailSignInError />}
                                        />
                                        <Route
                                            path="sso"
                                            element={<SSO />}
                                        />

                                        {/* Catch-all for protected routes - AppRouting handles /trainings, /assessments, /, etc. */}
                                        <Route
                                            path="*"
                                            element={
                                                <PrivateRoute>
                                                    {isGuest &&
                                                    (location.pathname!== "" &&  location.pathname !== "/")  &&
                                                    !infosecMatch &&
                                                    !miniHRAtype ? (
                                                        <Navigate
                                                            to={guestHomePath}
                                                        />
                                                    ) : (
                                                        <AppRouting
                                                            company_id={company_id}
                                                            subject_id={subject_id}
                                                            currentCompany={currentCompany}
                                                            appLoading={appLoading}
                                                            companyId={companyId}
                                                        />
                                                    )}
                                                </PrivateRoute>
                                            }
                                        />
                                    </Route>
                                </Routes>
                                <Snackbar
                                    variant={messageType}
                                    message={message || ''}
                                    open={messageShown}
                                    onClose={closeSnackbar}
                                />
                                <ConfirmationModal
                                    confirmationRequired={confirmationRequired}
                                    confirmationDescription={
                                        confirmationDescription
                                    }
                                    confirmationAction={confirmationAction}
                                    confirmationTitle={confirmationTitle}
                                    confirmationShowCancel={
                                        confirmationShowCancel
                                    }
                                    confirmationCancelText={
                                        confirmationCancelText
                                    }
                                    additionalCheckbox={additionalCheckbox}
                                    isConfirmationDelete={isConfirmationDelete}
                                    cancelConfirm={cancelConfirm}
                                    doConfirm={doConfirm}
                                />
                            </Fragment>
                        </DirectionProvider>
                        </I18nextProvider>
                    )}
                </ThemeProvider>
            </MuiThemeProvider>
        </StyledEngineProvider>
    );
}

export default observer(App);
